import { useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Spinner,
  Tab,
  Tabs,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  addCommitmentNote,
  deleteCommitmentNote,
  setCommitments,
} from 'common/src/api/commercialProgram';
import {
  PROGRAM_UNIT_SHORT,
  buildCommittedMatrix,
  buildShippedMatrix,
  commitmentsAt,
  formatProgramQuantity,
} from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  CommercialProgramVersion,
  ExporterReceiver,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { EmptyState } from '../../components/ui';
import NotesPopover from './NotesPopover';
import ImportExcelModal from './ImportExcelModal';
import AddProgramClientModal from './AddProgramClientModal';
import { exportProgramToExcel } from './exportProgram';
import type { CommercialProgramState } from './useCommercialProgram';

interface DetailViewProps {
  state: CommercialProgramState;
  program: CommercialProgram;
  /** Clientes del programa (con compromisos o recién agregados), filtrados. */
  receivers: ExporterReceiver[];
  /** Clientes de la exportadora que todavía no están en el programa. */
  addableReceivers: ExporterReceiver[];
  onAddReceiver: (receiverId: number) => void;
  canEdit: boolean;
  /** Versión elegida arriba: se muestra en solo lectura. null = vigente. */
  version: CommercialProgramVersion | null;
}

type Editing = { receiverId: number; week: number; value: string };

const parseQuantity = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : NaN;
};

/** Tabla cliente × semana del programa vigente, editable celda a celda. */
export default function DetailView({
  state,
  program,
  receivers,
  addableReceivers,
  onAddReceiver,
  canEdit,
  version,
}: DetailViewProps) {
  const { t } = useTranslation('commitments');
  const {
    weeks,
    rows,
    notes,
    userProfile,
    reloadProgramData,
    season,
    species,
    shipments,
    receiverIdOf,
    cutoffIndex,
    addReceiver,
  } = state;
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  // Enter y blur pueden llegar juntos: se guarda una sola vez.
  const savingRef = useRef(false);
  const [importOpen, setImportOpen] = useState(false);
  // Absoluto: "despachado / comprometido". %: cumplimiento de cada celda.
  const [display, setDisplay] = useState<'abs' | 'pct'>('abs');
  // Una versión guardada no se edita: se vuelve a "Vigente" para editar.
  const canEditCells = canEdit && !version;

  const matrix = useMemo(
    () =>
      buildCommittedMatrix(
        commitmentsAt(rows, version?.as_of),
        program,
        program.unit
      ),
    [rows, program, version]
  );
  const valueOf = (receiverId: number, week: number) =>
    matrix.get(receiverId)?.get(week) ?? 0;

  // Lo despachado, para comparar celda a celda al pasar el mouse.
  const { shipped } = useMemo(
    () =>
      buildShippedMatrix({
        shipments,
        receiverIdOf,
        weeks,
        unit: program.unit,
        dateBasis: version?.date_basis ?? program.date_basis,
        equivalences: program,
      }),
    [shipments, receiverIdOf, weeks, program, version]
  );

  const receiverTotals = useMemo(
    () =>
      new Map(
        receivers.map((r) => [
          r.id,
          weeks.reduce((sum, w) => sum + valueOf(r.id, w), 0),
        ])
      ),
    // valueOf solo depende de matrix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receivers, weeks, matrix]
  );
  const weekTotals = weeks.map((w) =>
    receivers.reduce((sum, r) => sum + valueOf(r.id, w), 0)
  );
  const grandTotal = weekTotals.reduce((a, b) => a + b, 0);

  // Despachado por semana y por cliente, para mostrarlo junto a lo comprometido.
  const shippedOf = (receiverId: number, week: number) =>
    shipped.get(receiverId)?.get(week) ?? 0;
  const weekShipped = weeks.map((w) =>
    receivers.reduce((sum, r) => sum + shippedOf(r.id, w), 0)
  );
  const grandShipped = weekShipped.reduce((a, b) => a + b, 0);

  const notesByReceiver = useMemo(() => {
    const map = new Map<number, typeof notes>();
    notes.forEach((n) =>
      map.set(n.exporter_receiver_id, [
        ...(map.get(n.exporter_receiver_id) || []),
        n,
      ])
    );
    return map;
  }, [notes]);

  /**
   * "despachado / comprometido". El despachado va en color solo en semanas
   * que ya terminaron; la semana en curso va en gris (todavía no es alerta) y
   * las que no llegan muestran un guion.
   */
  type WeekStage = 'past' | 'current' | 'future';
  const stageOf = (index: number): WeekStage =>
    index < cutoffIndex ? 'past' : index === cutoffIndex ? 'current' : 'future';
  const shippedTone = (shippedValue: number, committedValue: number) =>
    committedValue <= 0
      ? 'text-default-500'
      : shippedValue >= committedValue - 1e-9
        ? 'text-success-600'
        : shippedValue > 0
          ? 'text-warning-600'
          : 'text-danger-500';
  const pair = (
    shippedValue: number,
    committedValue: number,
    { stage, toned = true }: { stage: WeekStage; toned?: boolean }
  ) => (
    <span className='whitespace-nowrap'>
      <span
        className={
          stage === 'future'
            ? 'text-default-300'
            : stage === 'current'
              ? 'text-default-400'
              : toned
                ? shippedTone(shippedValue, committedValue)
                : ''
        }
      >
        {stage === 'future' ? '–' : fmt(shippedValue) || '0'}
      </span>
      <span className='text-default-300'> / </span>
      <span className={committedValue ? '' : 'text-default-300'}>
        {fmt(committedValue) || '–'}
      </span>
    </span>
  );
  // Lo mismo en %: despachado ÷ comprometido. Sin compromiso, lo despachado
  // va como "+N" en gris.
  const percent = (
    shippedValue: number,
    committedValue: number,
    { stage, toned = true }: { stage: WeekStage; toned?: boolean }
  ) => {
    if (stage === 'future') return <span className='text-default-300'>–</span>;
    if (committedValue <= 0)
      return (
        <span className='text-default-400'>
          {shippedValue ? `+${fmt(shippedValue)}` : '–'}
        </span>
      );
    return (
      <span
        className={
          stage === 'current'
            ? 'text-default-400'
            : toned
              ? shippedTone(shippedValue, committedValue)
              : ''
        }
      >
        {`${Math.round((shippedValue / committedValue) * 100)}%`}
      </span>
    );
  };
  const cell = display === 'pct' ? percent : pair;

  const fmt = (v: number) => (v ? formatProgramQuantity(v, program.unit) : '');
  const pct = (part: number, total: number) =>
    total > 0
      ? `${((part / total) * 100).toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`
      : '–';

  const saveEditing = async () => {
    if (!editing || savingRef.current) return;
    const quantity = parseQuantity(editing.value);
    if (Number.isNaN(quantity)) {
      toast.error(t('Cantidad inválida'));
      return;
    }
    if ((quantity ?? 0) === valueOf(editing.receiverId, editing.week)) {
      setEditing(null);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await setCommitments(program.id, [
        {
          exporter_receiver_id: editing.receiverId,
          week: editing.week,
          quantity,
        },
      ]);
      await reloadProgramData();
      setEditing(null);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(t('No se pudo guardar: {{error}}', { error: message }));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleAddNote = async (
    receiverId: number,
    week: number | null,
    content: string
  ) => {
    try {
      await addCommitmentNote({
        program_id: program.id,
        exporter_receiver_id: receiverId,
        week,
        content,
        created_by: userProfile?.id ?? null,
      });
      await reloadProgramData();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(t('No se pudo guardar: {{error}}', { error: message }));
    }
  };

  const handleDeleteNote = async (id: number) => {
    try {
      await deleteCommitmentNote(id);
      await reloadProgramData();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(t('No se pudo guardar: {{error}}', { error: message }));
    }
  };

  return (
    <Card shadow='sm'>
      <CardBody className='p-6'>
        <div className='mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div>
            <h3 className='text-lg font-semibold'>
              {t('Compromisos por cliente y semana')}
            </h3>
            <p className='text-xs text-default-500'>
              {t(
                display === 'pct'
                  ? version
                    ? 'Cada celda: % despachado de lo comprometido. Esta versión es solo de lectura; para editar vuelve a Vigente.'
                    : 'Cada celda: % despachado de lo comprometido. Haz clic para editar lo comprometido.'
                  : version
                    ? 'Cada celda: despachado / comprometido. Esta versión es solo de lectura; para editar vuelve a Vigente.'
                    : 'Cada celda: despachado / comprometido. Haz clic para editar lo comprometido.'
              )}
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Tabs
              size='sm'
              aria-label={t('Mostrar')}
              selectedKey={display}
              onSelectionChange={(key) => setDisplay(key as 'abs' | 'pct')}
            >
              <Tab key='abs' title={t('Absoluto')} />
              <Tab key='pct' title='%' />
            </Tabs>
            {canEditCells && (
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:user-plus' width={16} />}
                onPress={() => setAddClientOpen(true)}
              >
                {t('Agregar cliente')}
              </Button>
            )}
            {canEditCells && (
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:file-upload' width={16} />}
                onPress={() => setImportOpen(true)}
              >
                {t('Importar Excel')}
              </Button>
            )}
            <Button
              size='sm'
              variant='flat'
              startContent={<Icon icon='tabler:file-download' width={16} />}
              onPress={() =>
                exportProgramToExcel({
                  program,
                  receivers,
                  weeks,
                  matrix,
                  notes,
                  season,
                  speciesName: species?.name || '',
                  t,
                })
              }
            >
              {t('Exportar Excel')}
            </Button>
          </div>
        </div>

        {receivers.length === 0 ? (
          <EmptyState
            icon='tabler:users'
            title={
              state.receivers.length === 0
                ? t('No hay recibidores configurados aún.')
                : t('Todavía no hay clientes en el programa.')
            }
            description={
              canEditCells
                ? t(
                    'Agrega los clientes a los que les vas a cargar fruta, o importa el programa desde Excel.'
                  )
                : undefined
            }
            action={
              canEditCells && (
                <Button
                  color='primary'
                  startContent={<Icon icon='tabler:user-plus' width={16} />}
                  onPress={() => setAddClientOpen(true)}
                >
                  {t('Agregar cliente')}
                </Button>
              )
            }
          />
        ) : (
          // Con muchos clientes se hace scroll dentro de la tabla; los encabezados
          // y el TOTAL quedan fijos.
          <div className='max-h-[60vh] overflow-auto'>
            <table className='w-full border-separate border-spacing-0 text-sm'>
              <thead>
                <tr className='text-xs text-default-500'>
                  <th className='sticky left-0 top-0 z-30 bg-content1 px-2 py-2 text-left font-medium'>
                    {t('CLIENTE')}
                  </th>
                  {weeks.map((w) => (
                    <th
                      key={w}
                      className='sticky top-0 z-20 bg-content1 px-1 py-2 text-center font-medium'
                    >{`S${w}`}</th>
                  ))}
                  <th className='sticky top-0 z-20 bg-content1 px-2 py-2 text-right font-semibold'>
                    {t('TOTAL')}
                  </th>
                  <th className='sticky top-0 z-20 bg-content1 px-2 py-2 text-right font-medium'>
                    {t('% DEL PROGRAMA')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {receivers.map((r) => {
                  const receiverNotes = notesByReceiver.get(r.id) || [];
                  const total = receiverTotals.get(r.id) || 0;
                  const receiverShipped = weeks.reduce(
                    (sum, w) => sum + shippedOf(r.id, w),
                    0
                  );
                  return (
                    <tr key={r.id} className='border-t border-default-100'>
                      <td className='sticky left-0 z-10 border-t border-default-100 bg-content1 px-2 py-1'>
                        <div className='flex items-center gap-1'>
                          <span
                            className='max-w-[200px] truncate font-medium'
                            title={r.name}
                          >
                            {r.name}
                          </span>
                          <NotesPopover
                            receiverName={r.name}
                            notes={receiverNotes}
                            weeks={weeks}
                            canEdit={canEdit}
                            onAdd={(week, content) =>
                              handleAddNote(r.id, week, content)
                            }
                            onDelete={handleDeleteNote}
                          />
                        </div>
                      </td>
                      {weeks.map((w, i) => {
                        const value = valueOf(r.id, w);
                        const weekNotes = receiverNotes.filter(
                          (n) => n.week === w
                        );
                        const isEditing =
                          editing?.receiverId === r.id && editing.week === w;
                        // Al pasar el mouse: comprometido vs. despachado y la nota de la semana.
                        const shippedValue = shipped.get(r.id)?.get(w) ?? 0;
                        // Semanas que ya pasaron (o la actual): ahí se compara.
                        const isPast = i <= cutoffIndex;
                        const showCompare = value > 0 || shippedValue > 0;
                        const tooltip =
                          showCompare || weekNotes.length ? (
                            <div className='max-w-xs py-1 text-left text-sm'>
                              {showCompare && (
                                <div className='mb-1 grid grid-cols-2 gap-x-3'>
                                  <span className='text-default-500'>
                                    {t('Comprometido')}
                                  </span>
                                  <span className='text-right font-medium'>
                                    {fmt(value) || '0'}{' '}
                                    {PROGRAM_UNIT_SHORT[program.unit]}
                                  </span>
                                  <span className='text-default-500'>
                                    {t('Despachado')}
                                  </span>
                                  <span className='text-right font-medium'>
                                    {isPast
                                      ? `${fmt(shippedValue) || '0'} ${PROGRAM_UNIT_SHORT[program.unit]}`
                                      : t('aún no')}
                                  </span>
                                  {isPast && value > 0 && (
                                    <>
                                      <span className='text-default-500'>
                                        {t('Cumplimiento')}
                                      </span>
                                      <span className='text-right font-semibold'>
                                        {pct(shippedValue, value)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                              {weekNotes.map((n) => (
                                <div
                                  key={n.id}
                                  className='mt-1.5 border-t border-default-200 pt-1.5'
                                >
                                  <p className='whitespace-pre-wrap'>
                                    {n.content}
                                  </p>
                                  {n.author && (
                                    <p className='text-xs text-default-400'>
                                      {n.author}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : null;
                        return (
                          <td
                            key={w}
                            className='border-t border-default-100 p-0.5 text-center'
                          >
                            {isEditing ? (
                              <div className='flex items-center justify-center gap-0.5'>
                                <input
                                  autoFocus
                                  inputMode='decimal'
                                  aria-label={`${r.name} S${w}`}
                                  className='w-16 rounded-md border border-primary bg-background px-1 py-1 text-center text-sm outline-none'
                                  value={editing.value}
                                  onChange={(e) =>
                                    setEditing({
                                      ...editing,
                                      value: e.target.value,
                                    })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEditing();
                                    if (e.key === 'Escape') setEditing(null);
                                  }}
                                  onBlur={saveEditing}
                                  disabled={saving}
                                />
                                {saving && <Spinner size='sm' />}
                              </div>
                            ) : (
                              <Tooltip
                                content={tooltip}
                                isDisabled={!tooltip}
                                placement='top'
                                delay={150}
                                closeDelay={0}
                              >
                                <button
                                  type='button'
                                  disabled={!canEditCells}
                                  onClick={() =>
                                    setEditing({
                                      receiverId: r.id,
                                      week: w,
                                      value: value
                                        ? String(Number(value.toFixed(4)))
                                        : '',
                                    })
                                  }
                                  className={`relative w-full min-w-[4.5rem] rounded px-1 py-1.5 text-xs ${
                                    canEditCells ? 'hover:bg-default-100' : ''
                                  } ${value || shippedValue ? 'text-foreground' : 'text-default-300'}`}
                                >
                                  {value || (isPast && shippedValue)
                                    ? cell(shippedValue, value, {
                                        stage: stageOf(i),
                                      })
                                    : '·'}
                                  {weekNotes.length > 0 && (
                                    <span className='absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary' />
                                  )}
                                </button>
                              </Tooltip>
                            )}
                          </td>
                        );
                      })}
                      <td className='border-t border-default-100 px-2 text-right text-xs font-semibold'>
                        {total || receiverShipped
                          ? cell(receiverShipped, total, {
                              stage: 'past',
                              toned: false,
                            })
                          : ''}
                      </td>
                      <td className='border-t border-default-100 px-2 text-right text-default-500'>
                        {total ? pct(total, grandTotal) : ''}
                      </td>
                    </tr>
                  );
                })}
                <tr className='font-semibold'>
                  <td className='sticky bottom-0 left-0 z-30 border-t-2 border-default-200 bg-content1 px-2 py-2'>
                    {t('TOTAL')}
                  </td>
                  {weekTotals.map((v, i) => (
                    <td
                      key={weeks[i]}
                      className='sticky bottom-0 z-20 border-t-2 border-default-200 bg-content1 px-1 text-center text-xs'
                    >
                      {v || (i <= cutoffIndex && weekShipped[i])
                        ? cell(weekShipped[i], v, { stage: stageOf(i) })
                        : ''}
                    </td>
                  ))}
                  <td className='sticky bottom-0 z-20 border-t-2 border-default-200 bg-content1 px-2 text-right text-xs'>
                    {cell(grandShipped, grandTotal, {
                      stage: 'past',
                      toned: false,
                    })}
                  </td>
                  <td className='sticky bottom-0 z-20 border-t-2 border-default-200 bg-content1 px-2 text-right text-default-500'>
                    {grandTotal ? '100%' : '–'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      {addClientOpen && userProfile?.exporter_id && (
        <AddProgramClientModal
          isOpen={addClientOpen}
          onClose={() => setAddClientOpen(false)}
          exporterId={userProfile.exporter_id}
          addableReceivers={addableReceivers}
          allReceivers={state.receivers}
          onAdd={onAddReceiver}
          onCreated={(receiver) => {
            addReceiver(receiver);
            onAddReceiver(receiver.id);
          }}
        />
      )}
      {importOpen && (
        <ImportExcelModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          state={state}
          program={program}
          currentMatrix={matrix}
        />
      )}
    </Card>
  );
}
